import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'class' },

  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#6e56cf', light: '#baa7ff', dark: '#654dc4', contrastText: '#fff' },
        secondary: { main: '#3e63dd', light: '#abbdf9', dark: '#3358d4', contrastText: '#fff' },
        error: { main: '#e5484d', light: '#ff9592', dark: '#ce2c31', contrastText: '#fff' },
        warning: { main: '#ffc53d', light: '#ffea70', dark: '#ab6400', contrastText: '#1a1400' },
        success: { main: '#30a46c', light: '#3dd68c', dark: '#218358', contrastText: '#fff' },
        info: { main: '#0090ff', light: '#70b8ff', dark: '#0d74ce', contrastText: '#fff' },
        background: { default: '#fcfcfd', paper: '#ffffff' },
        text: { primary: '#1c2024', secondary: '#60646c', disabled: '#8b8d98' },
        divider: '#e0e1e6',
        action: {
          hover: 'rgba(0,0,0,0.04)',
          selected: 'rgba(110,86,207,0.08)',
          focus: 'rgba(110,86,207,0.12)',
        },
      },
    },
    dark: {
      palette: {
        primary: { main: '#7d66d9', light: '#baa7ff', dark: '#6e56cf', contrastText: '#fff' },
        secondary: { main: '#5472e4', light: '#9eb1ff', dark: '#3e63dd', contrastText: '#fff' },
        error: { main: '#ec5d5e', light: '#ff9592', dark: '#e5484d', contrastText: '#fff' },
        warning: { main: '#ffd60a', light: '#ffe7b3', dark: '#ffca16', contrastText: '#1a1400' },
        success: { main: '#33b074', light: '#3dd68c', dark: '#30a46c', contrastText: '#fff' },
        info: { main: '#3b9eff', light: '#70b8ff', dark: '#0090ff', contrastText: '#fff' },
        background: { default: '#111113', paper: '#18191b' },
        text: { primary: '#edeef0', secondary: '#b0b4ba', disabled: '#696e77' },
        divider: '#2e3135',
        action: {
          hover: 'rgba(255,255,255,0.06)',
          selected: 'rgba(125,102,217,0.15)',
          focus: 'rgba(125,102,217,0.2)',
        },
      },
    },
  },

  shape: { borderRadius: 6 },

  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.33, letterSpacing: '-0.01em' },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.005em' },
    h4: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.41 },
    h5: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.47 },
    h6: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.54, letterSpacing: '0.005em' },
    body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.57 },
    body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.54 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.57 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.54 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.01em' },
    overline: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.45, letterSpacing: '0.08em', textTransform: 'uppercase' },
    button: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1, textTransform: 'none' },
  },

  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05)',
    '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.05)',
    '0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.05)',
    '0 20px 25px rgba(0,0,0,0.10), 0 10px 10px rgba(0,0,0,0.04)',
    ...Array(19).fill('0 20px 25px rgba(0,0,0,0.10), 0 10px 10px rgba(0,0,0,0.04)'),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        body: {
          fontFeatureSettings: '"cv02","cv03","cv04","cv11"',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 6 },
        sizeSmall: { height: 28, padding: '0 10px', fontSize: 13 },
        sizeMedium: { height: 34, padding: '0 14px', fontSize: 14 },
        sizeLarge: { height: 40, padding: '0 18px', fontSize: 14 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 6 },
        input: { padding: '8px 12px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 14 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 8,
          border: `1px solid ${t.palette.divider}`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12, backgroundImage: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 4, fontWeight: 500 },
        sizeSmall: { height: 22, fontSize: 12 },
        sizeMedium: { height: 26, fontSize: 13 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: 13 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: 4, fontSize: 12, fontWeight: 400 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 13 },
        head: { fontWeight: 600 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, fontSize: 14 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500 },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundColor: t.palette.background.paper,
          color: t.palette.text.primary,
          borderBottom: `1px solid ${t.palette.divider}`,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none' },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { fontSize: 14 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: 14 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4 },
      },
    },
  },
});

export default theme;
